// src/modules/maintenance/components/MaintenanceModals.js
// Periyodik bakım modal bileşenleri

import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, Stack, Alert,
    FormControl, InputLabel, Select, MenuItem, CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { calculateNextMaintenanceDate } from '../utils/maintenanceUtils';

/**
 * Müşteri Geldi - Bakım Tamamlandı Modal
 */
export const GeldiModal = ({
    open,
    onClose,
    device,
    onConfirm
}) => {
    const [sonrakiBakimSuresi, setSonrakiBakimSuresi] = useState(6);
    const [sonrakiBakimBirimi, setSonrakiBakimBirimi] = useState('ay');
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        setIsLoading(true);
        const result = await onConfirm(device, sonrakiBakimSuresi, sonrakiBakimBirimi);
        setIsLoading(false);
        if (result.success) {
            alert(result.message);
            onClose();
        } else {
            alert(result.message);
        }
    };

    const previewDate = calculateNextMaintenanceDate(sonrakiBakimSuresi, sonrakiBakimBirimi);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    Bakım Tamamlandı
                </Box>
            </DialogTitle>
            <DialogContent>
                {device && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            <b>Müşteri:</b> {device.musteriAdSoyad}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <b>Cihaz:</b> {device.marka} {device.model} ({device.seriNumarasi})
                        </Typography>
                    </Box>
                )}
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Sonraki bakım ne zaman yapılacak?
                </Typography>
                <Stack direction="row" spacing={2}>
                    <TextField
                        type="number"
                        label="Süre"
                        value={sonrakiBakimSuresi}
                        onChange={(e) => setSonrakiBakimSuresi(Math.max(1, Number(e.target.value)))}
                        inputProps={{ min: 1 }}
                        sx={{ width: 100 }}
                    />
                    <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Birim</InputLabel>
                        <Select
                            value={sonrakiBakimBirimi}
                            label="Birim"
                            onChange={(e) => setSonrakiBakimBirimi(e.target.value)}
                        >
                            <MenuItem value="gün">Gün</MenuItem>
                            <MenuItem value="hafta">Hafta</MenuItem>
                            <MenuItem value="ay">Ay</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
                <Alert severity="info" sx={{ mt: 2 }}>
                    Sonraki bakım tarihi: <b>{previewDate.toLocaleDateString('tr-TR')}</b>
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>İptal</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="success"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                >
                    {isLoading ? 'Kaydediliyor...' : 'Bakımı Kaydet'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Manuel SMS Gönderme Modal
 */
export const SmsModal = ({
    open,
    onClose,
    device,
    onSend
}) => {
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    // Modal açıldığında varsayılan mesajı ayarla
    React.useEffect(() => {
        if (open && device) {
            setMessage(`Sayın ${device.musteriAdSoyad}, ${device.marka} ${device.model} marka cihazınızın periyodik bakım zamanı gelmiştir. Randevu için bizimle iletişime geçiniz. Digitalpedi Medikal`);
        }
    }, [open, device]);

    const handleSend = async () => {
        setIsSending(true);
        const result = await onSend(device, message);
        setIsSending(false);
        if (result.success) {
            alert(result.message);
            onClose();
        } else {
            alert(result.message);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsActiveIcon color="primary" />
                    Manuel SMS Gönder
                </Box>
            </DialogTitle>
            <DialogContent>
                {device && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            <b>Müşteri:</b> {device.musteriAdSoyad}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <b>Cihaz:</b> {device.marka} {device.model} ({device.seriNumarasi})
                        </Typography>
                    </Box>
                )}
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="SMS Mesajı"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    helperText={`${message.length} karakter`}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSending}>İptal</Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    disabled={isSending || !message}
                    startIcon={isSending ? <CircularProgress size={20} /> : <NotificationsActiveIcon />}
                >
                    {isSending ? 'Gönderiliyor...' : 'SMS Gönder'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
